/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {WarningsEnum} from '../../warnings/warnings.enum';
import {MandatoryMessageTypeEnum} from './mandatory-message-type.enum';
import {InputComponent} from '../input/input.component';

@Injectable({
    providedIn: 'root'
})
export class InputMessagesService {
    private readonly labelMap = new Map<MandatoryMessageTypeEnum, string>([
        [MandatoryMessageTypeEnum.SAVE, 'Mandatory field for save'],
        [MandatoryMessageTypeEnum.SAVE_AND_SEND, 'Mandatory field for save and send'],
        [MandatoryMessageTypeEnum.SEND, 'Mandatory field for send'],
        [MandatoryMessageTypeEnum.CREATE, 'Mandatory field to create'],
        [MandatoryMessageTypeEnum.APPLY, 'Mandatory field to apply'],
        [MandatoryMessageTypeEnum.NUMERIC, 'Be aware that this is a numeric field']
    ]);

    constructor() {
    }

    mandatoryWarnings(control: AbstractControl, messageMap: Map<WarningsEnum, string[]>, type: MandatoryMessageTypeEnum): void {
        const message = this.mandatoryWarningsLabel(type);
        if (!messageMap.has(
                WarningsEnum.WARNING_CUSTOM)) {
            if (!control.disabled && control.touched && control.value == null || control.value === '') {
                if (messageMap.has(WarningsEnum.WARNING) && !messageMap.get(WarningsEnum.WARNING)
                        .includes(message)) {
                    messageMap.delete(WarningsEnum.WARNING);
                    messageMap.set(WarningsEnum.WARNING, [message]);
                } else {
                    messageMap.set(WarningsEnum.WARNING, [message]);
                }
            } else {
                if (messageMap.get(WarningsEnum.WARNING) && messageMap.get(WarningsEnum.WARNING)
                        .includes(message)) {
                    messageMap.get(WarningsEnum.WARNING)
                            .splice(messageMap.get(WarningsEnum.WARNING)
                                    .indexOf(message));
                }
            }
        }
    }

    invalidInputValueError(control: AbstractControl,
                           messageMap: Map<WarningsEnum, string[]>,
                           label: string,
                           example?: string): void {
        if (messageMap.has(WarningsEnum.ERROR) && messageMap.get(WarningsEnum.ERROR).length > 0) {
            if (!control.invalid) {
                messageMap.set(WarningsEnum.ERROR, []);
            } else {
                return;
            }
        }
        if (control.invalid) {
            messageMap.set(WarningsEnum.ERROR, [this.invalidValueErrorLabel(label, example)]);
        } else {
            messageMap.set(WarningsEnum.ERROR, []);
        }
    }

    customInputValueWarning(messageMap: Map<WarningsEnum, string[]>,
                            label: string): void {
        if (label != null && label !== '') {
            messageMap.delete(WarningsEnum.WARNING);
            messageMap.set(WarningsEnum.WARNING_CUSTOM, [label]);
        } else {
            messageMap.delete(WarningsEnum.WARNING_CUSTOM);
        }
    }

    fatherInputValueWarning(father: InputComponent, input: InputComponent, messageMap: Map<WarningsEnum, string[]>): void {
        if (input.inputControl?.value != null && input.inputControl.value !== '') {
            messageMap.set(WarningsEnum.WARNING, [`Please enter a valid ${father.label}`]);
        }
        input.disable();
    }

    /**
     * Returns an mandatory warning label
     * @param type the warning message type
     */
    private mandatoryWarningsLabel(type: MandatoryMessageTypeEnum): string {
        return this.labelMap.get(type);
    }

    /**
     * Returns an invalid input value error message
     * @param field The name of the input field
     * @param example A correct value example
     * @returns The error message string
     */
    private invalidValueErrorLabel(field: string, example?: string): string {
        if (example) {
            return `Please enter a valid ${field}. e.g. ${example}`;
        }
        return `Please enter a valid ${field}`;
    }
}

